/**
 * Serialize / deserialize Fabric annotation overlays per PDF page.
 */

import type { fabric } from 'fabric';

export type FabricJson = Record<string, unknown>;

export function emptyFabricJson(width: number, height: number): FabricJson {
  return {
    version: '5.3.0',
    objects: [],
    background: '',
    width,
    height,
  };
}

/** Rasterize a live image element to a durable data URL (survives blob: revoke). */
function elementToDataUrl(
  el: HTMLImageElement | HTMLCanvasElement
): string | null {
  try {
    const w =
      el instanceof HTMLImageElement
        ? el.naturalWidth || el.width
        : el.width;
    const h =
      el instanceof HTMLImageElement
        ? el.naturalHeight || el.height
        : el.height;
    if (!w || !h) return null;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(el, 0, 0);
    return c.toDataURL('image/png');
  } catch {
    // Tainted canvas / revoked blob — caller keeps original src
    return null;
  }
}

/**
 * Ensure image objects in Fabric JSON use embedded data: URLs.
 * blob:/http(s) srcs break Finalize when the URL is revoked or CORS-blocked.
 */
export function embedAnnotationImageSrcs(
  json: FabricJson,
  liveObjects?: fabric.Object[]
): FabricJson {
  const objects = json.objects;
  if (!Array.isArray(objects)) return json;

  const nextObjects = objects.map((raw, index) => {
    if (!raw || typeof raw !== 'object') return raw;
    const row = raw as Record<string, unknown>;
    const type = String(row.type || '').toLowerCase();
    if (type !== 'image') return raw;

    const src = typeof row.src === 'string' ? row.src : '';
    if (src.startsWith('data:image/')) return raw;

    // Prefer pixels from the live Fabric object (works even if blob: was revoked)
    const live = liveObjects?.[index] as fabric.Image | undefined;
    const el = live?.getElement?.() as
      | HTMLImageElement
      | HTMLCanvasElement
      | undefined;
    if (el) {
      const embedded = elementToDataUrl(el);
      if (embedded) {
        return { ...row, src: embedded, crossOrigin: 'anonymous' };
      }
    }

    return raw;
  });

  return { ...json, objects: nextObjects };
}

export function canvasToAnnotationJson(canvas: fabric.Canvas): FabricJson {
  const json = canvas.toJSON([
    'selectable',
    'evented',
    'name',
    'pdfAnnotation',
  ]) as FabricJson;
  const withImages = embedAnnotationImageSrcs(json, canvas.getObjects());
  // Deep-clone so history / drafts cannot share mutable object arrays
  const cloned = JSON.parse(JSON.stringify(withImages)) as FabricJson;
  return {
    ...cloned,
    width: canvas.getWidth(),
    height: canvas.getHeight(),
  };
}

async function awaitLoadFromJSON(
  canvas: fabric.Canvas,
  json: FabricJson
): Promise<void> {
  const payload = JSON.parse(JSON.stringify(json)) as FabricJson;
  if (payload.background === '') delete payload.background;
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    try {
      // Fabric 5: callback-based (returns canvas). Some builds may return a Promise.
      const result = canvas.loadFromJSON(payload, finish) as unknown;
      if (
        result &&
        typeof result === 'object' &&
        typeof (result as Promise<unknown>).then === 'function'
      ) {
        void (result as Promise<unknown>).then(finish).catch(reject);
      }
    } catch (err) {
      reject(err);
    }
  });

  // Allow enliven + layout to settle
  await new Promise<void>((r) => requestAnimationFrame(() => r()));
}

export function loadAnnotationJson(
  canvas: fabric.Canvas,
  json: FabricJson | null | undefined
): Promise<void> {
  return (async () => {
    canvas.clear();
    if (!json || !Array.isArray(json.objects) || json.objects.length === 0) {
      canvas.requestRenderAll();
      return;
    }
    await awaitLoadFromJSON(canvas, json);
    canvas.requestRenderAll();
  })();
}

/** Scale Fabric objects when the viewport size changes relative to stored JSON. */
export function scaleAnnotationCanvas(
  canvas: fabric.Canvas,
  fromWidth: number,
  fromHeight: number,
  toWidth: number,
  toHeight: number
): void {
  if (!fromWidth || !fromHeight || fromWidth === toWidth && fromHeight === toHeight) {
    canvas.setWidth(toWidth);
    canvas.setHeight(toHeight);
    return;
  }
  const sx = toWidth / fromWidth;
  const sy = toHeight / fromHeight;
  canvas.setWidth(toWidth);
  canvas.setHeight(toHeight);
  canvas.getObjects().forEach((obj) => {
    obj.scaleX = (obj.scaleX || 1) * sx;
    obj.scaleY = (obj.scaleY || 1) * sy;
    obj.left = (obj.left || 0) * sx;
    obj.top = (obj.top || 0) * sy;
    obj.setCoords();
  });
  canvas.requestRenderAll();
}
